import { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useUIStore } from '@/stores';
import type { PageKey } from '@/stores/uiStore';

interface ListDateCarouselProps {
  pageKey: PageKey;
}

/**
 * ListDateCarousel - Appears ONLY in the Today tab
 *
 * Allows user to change the "reference date" which affects:
 * - Today tab: shows items due on reference date
 * - Past Week: shows items due in 7 days BEFORE reference date
 * - Next Week: shows items due in 7 days AFTER reference date
 */
export function ListDateCarousel({ pageKey }: ListDateCarouselProps) {
  const { setReferenceDate, resetReferenceDate, getReferenceDate } = useUIStore();
  const referenceDate = getReferenceDate(pageKey);

  // Calculate display info
  const { dateLabel, isToday } = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const refDateNormalized = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    let label: string;
    if (refDateNormalized.getTime() === today.getTime()) {
      label = 'Today';
    } else if (refDateNormalized.getTime() === yesterday.getTime()) {
      label = 'Yesterday';
    } else if (refDateNormalized.getTime() === tomorrow.getTime()) {
      label = 'Tomorrow';
    } else {
      label = refDateNormalized.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    }

    return {
      dateLabel: label,
      isToday: refDateNormalized.getTime() === today.getTime(),
    };
  }, [referenceDate]);

  const handlePrevious = () => {
    const newDate = new Date(referenceDate);
    newDate.setDate(newDate.getDate() - 1);
    setReferenceDate(pageKey, newDate);
  };

  const handleNext = () => {
    const newDate = new Date(referenceDate);
    newDate.setDate(newDate.getDate() + 1);
    setReferenceDate(pageKey, newDate);
  };

  const handleReset = () => {
    resetReferenceDate(pageKey);
  };

  return (
    <div className="flex items-center gap-2">
      {/* Reset button - only show when not on today */}
      {!isToday && (
        <button
          onClick={handleReset}
          className="px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
          title="Reset to today"
        >
          Reset
        </button>
      )}

      <button
        onClick={handlePrevious}
        className="p-1.5 rounded-lg transition-colors text-gray-500 hover:text-gray-700 hover:bg-gray-100"
        title="Previous day"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      <span
        className="px-3 py-1 text-sm font-medium rounded-lg min-w-[100px] text-center text-gray-900 bg-gray-100"
      >
        {dateLabel}
      </span>

      <button
        onClick={handleNext}
        className="p-1.5 rounded-lg transition-colors text-gray-500 hover:text-gray-700 hover:bg-gray-100"
        title="Next day"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}
